// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateTime } from 'luxon';

/**
 * Maps a billing frequency string to a Luxon duration object.
 */
function frequencyToInterval(
  frequency: string | null
): Record<string, number> | null {
  switch (frequency?.toUpperCase()) {
    case 'DAILY':
      return { days: 1 };
    case 'WEEKLY':
      return { weeks: 1 };
    case 'BIWEEKLY':
      return { weeks: 2 };
    case 'MONTHLY':
      return { months: 1 };
    case 'QUARTERLY':
      return { months: 3 };
    case 'SEMIANNUALLY':
      return { months: 6 };
    case 'ANNUALLY':
      return { years: 1 };
    case 'BIENNIALLY':
      return { years: 2 };
    case 'TRIENNIALLY':
      return { years: 3 };
    case 'QUADRENNIALLY':
      return { years: 4 };
    case 'QUINQUENNIALLY':
      return { years: 5 };
    default:
      return null;
  }
}

interface ComputeContinuationDatesInput {
  previousEndDate: DateTime | null;
  billingFrequency: string | null;
  projectEnd: DateTime | null;
}

interface ComputeContinuationDatesResult {
  startDate: DateTime | null;
  endDate: DateTime | null;
}

/**
 * Computes the start and end dates for a continuation line item based on the
 * previous item's end date, the billing frequency, and the project end date.
 */
export function computeContinuationDates(
  input: ComputeContinuationDatesInput
): ComputeContinuationDatesResult {
  if (!input.previousEndDate) {
    return { startDate: null, endDate: null };
  }

  const nextStartDate = input.previousEndDate.plus({ days: 1 });

  let endDate: DateTime | null = null;
  if (input.projectEnd && nextStartDate <= input.projectEnd) {
    endDate = input.projectEnd;
  } else {
    const interval = frequencyToInterval(input.billingFrequency);
    if (interval) {
      endDate = nextStartDate.plus(interval).minus({ days: 1 });
    }
  }

  return {
    startDate: nextStartDate,
    endDate,
  };
}
